/* Core & libraries */
import React, {useEffect, useState} from 'react'
import Dropzone from 'react-dropzone-uploader-with-credentials'
import { useTheme } from "react-jss";
import Row from "react-bootstrap/Row";
import { FormattedMessage, useIntl } from "react-intl";
import Col from "react-bootstrap/Col";

/* Helpers, constants, custom methods, internal libraries */
import { useNotificationContext } from "../../../../contexts/notificationContext";
import { useSpinnersUpdateContext } from '../../../../contexts/spinnersContext';
import { useUserContext } from "../../../../contexts/userContext";
import { AIRLINES } from "../../../../constants/appConstants";
import { getCookieValue } from "../../../../helpers/general";
import { UPLOAD_GUEST_PROFILES_ENDPOINT } from "../../../../endpoints/endpoints";
import { API_HOST, API_PROTOCOL } from "../../../../constants/appConstants";
import { csvSpinners } from '../../../../constants/appConstants';
/* Components */
import UCButton from "../../../buttons";
import CustomPreview from "./customPreview/customPreview";
import CustomInput from "./customInput/customInput";
import CustomSubmitButton from "./customSubmitButton/customSubmitButton";

/* Styles */
import styles from "./styles";


const validateHeaders = (columns, intl, providerCode) => {
  const requiredColumns = providerCode === AIRLINES.AC
    ? ['First name', 'Last name']
    : ['First name', 'Last name', 'Date of birth', 'Gender'];
  const optionalColumns = [
    "Middle name", "Suffix", "Preferred name",
    'Phone country code', 'Phone number',
    "Known traveler number", "Redress number",
    "Email address", "Membership number", "Airline code", "Country of citizenship",
    "Country of residence", "Passport number", "Passport expiration date", "Label"
  ];
  const allowedColumns = [...requiredColumns, ...optionalColumns];

  const unknownColumns = columns.filter(
    (col) => col && !allowedColumns.some(
      (allowed) => allowed.toLowerCase() === col.trim().toLowerCase()
    )
  );

  if (unknownColumns.length > 0) {
    return intl.formatMessage({ id: 'dropzone.validation.unknownColumns' }, { columns: unknownColumns.join(', ') });
  }

  const missingColumns = requiredColumns.filter(
    (requiredCol) => !columns.some((col) => col.toLowerCase() === requiredCol.toLowerCase())
  );

  if (missingColumns.length > 0) {
    return intl.formatMessage({ id: 'dropzone.validation.missingColumns' }, { columns: missingColumns.join(', ') });
  }

  const incorrectHeaders = columns.filter((col) => {
    const expected = requiredColumns.find((req) => req.toLowerCase() === col.toLowerCase());
    return expected && col !== expected;
  });

  if (incorrectHeaders.length > 0) {
    return intl.formatMessage({ id: 'dropzone.validation.incorrectHeader' }, { columns: requiredColumns.join(', ') });
  }

  return null;
};

const parseFile = (file, intl, providerCode) => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    const MAX_ROWS = 500;

    reader.onloadend = () => {
      const result = reader.result;
      try {
        const lines = result.split('\n').map((line) => line.trim()).filter(Boolean);
        if (lines.length <= 1) return resolve(intl.formatMessage({ id: 'dropzone.validation.uploadGuest.noData' }));
        if (lines.length > MAX_ROWS + 1) return resolve(intl.formatMessage({ id: 'dropzone.validation.maxRows' }, { maxRows: MAX_ROWS }));

        const columns = lines[0].split(',').map((col) => col.trim());
        const headerError = validateHeaders(columns, intl, providerCode);
        if (headerError) return resolve(headerError);
        resolve(false);
      } catch (err) {
        resolve(intl.formatMessage({ id: 'notifications.uploadGuest.generic' }));
      }
    };

    reader.readAsText(file);
  });
};

const GuestProfilesDropzone = (props) => {
  const userDetails = useUserContext();
  const companyEntityId = userDetails?.userInfo?.companyInfo?.companyEntityId;
  const providerCode = userDetails?.userInfo?.providerInfo?.providerCode;
  const { setShowModal, isUploading, setIsUploading, callback } = props;
  const theme = useTheme();
  const classes = styles({ theme });
  const intl = useIntl();

  const { setNotification } = useNotificationContext();
  const setSpinner = useSpinnersUpdateContext();

  const [validationError, setValidationError] = useState(null);
  const SUPPORTED_ERROR_CODES = [
    "7001","7002","7003","7004","7005","7000","7014","9990",
    "9991","9992","9987","9988","9993","9984","9985","9997",
    "9994","9998","9989","9995","9996","9999","9975","9976","9983"
  ];

  useEffect(() => {
    setSpinner({
      [csvSpinners.guestProfilesCSV]: false
    })
  }, []);

  // specify upload params and url for your files
  const getUploadParams = ({ meta, file }) => {
    const body = new FormData()
    body.append('file', file)
    return {
      url: API_PROTOCOL + "://" + API_HOST + UPLOAD_GUEST_PROFILES_ENDPOINT.replace("{providerCode}", providerCode.toLowerCase()).replace("{companyEntityId}", companyEntityId),
      body,
      headers: {
        'X-XSRF-TOKEN': getCookieValue('XSRF-TOKEN')
      }
    }
  }

  // called every time a file's `status` changes
  const handleChangeStatus = ({ meta, file, xhr }, status) => {
    if (status === "uploading") {
      setSpinner({
        [csvSpinners.guestProfilesCSV]: true
      })

      setIsUploading(true);
    } else if (["error", "headers_received", "exception", "error_upload"].includes(status)) {
      setIsUploading(false);
    }

    if (['exception_upload', 'error_upload'].includes(status)) {
      xhr.onload = function () {
        let errorMessage;
        if(xhr?.status === 403) {
          errorMessage = <FormattedMessage id="notifications.uploadGuest.403" />
        } else if (xhr?.status === 500) {
          errorMessage = <FormattedMessage id="notifications.uploadGuest.generic" />
        } else if(xhr?.status === 400 ) {
          const errorCode = JSON.parse(xhr?.response)?.errors?.[0]?.errorCode;
          if (SUPPORTED_ERROR_CODES.includes(errorCode)) {
            errorMessage = <FormattedMessage id={`notifications.uploadGuest.${errorCode}`} />
          } else {
            errorMessage = <FormattedMessage id={`notifications.uploadGuest.generic`} />
          }
        } else {
          errorMessage = <FormattedMessage id="notifications.uploadGuest.generic" />
        }
        setValidationError(errorMessage);
      };
      setSpinner({
        [csvSpinners.guestProfilesCSV]: false
      })
    }

    if (status === "preparing") {
      setValidationError(null)
      setIsUploading(false);

      return parseFile(file, intl, providerCode).then((errorMessage) => {
        if (errorMessage) {
          setSpinner({
            [csvSpinners.guestProfilesCSV]: false,
          });
          setIsUploading(false);
          setValidationError(errorMessage);
          return errorMessage;
        }
        meta.hasRequiredColumns = true;
        return false;
      });

    } else if (status === "done") {
      setSpinner({
        [csvSpinners.guestProfilesCSV]: false
      })
      setIsUploading(false);
      setNotification(
        "success",
        <FormattedMessage id="notifications.uploadGuest.successTitle"/>,
        <FormattedMessage id="notifications.uploadGuest.successText"/>
      );
      callback({page: 1, filters: {LABEL: []}});
      setShowModal(false);
    } else if (status === "error") {
      setValidationError(<FormattedMessage id="notifications.uploadGuest.generic"/>)
    }
  }

  // receives array of files that are done uploading when submit button is clicked
  const handleSubmit = (files, allFiles) => {
    allFiles.forEach(f => f.restart())
  }
  const customLayout = ({ input, previews, submitButton, dropzoneProps, files, extra: { maxFiles } }) => {
    return (
      <div>
        {previews}
        {files.length < maxFiles && <div {...dropzoneProps}>{input}</div>}
        <p className={classes.details}>
          <FormattedMessage id="guestHeader.modalUpload.details" />
        </p>
        <p className={classes.optionDetails}>
          <div>
            <span className={classes.element}>
              <FormattedMessage id="guestHeader.modalUpload.required" />
            </span>
            <FormattedMessage id={providerCode === AIRLINES.AC ? "guestHeader.modalUpload.emailDescriptionAC" : "guestHeader.modalUpload.emailDescription"} />
          </div>
        </p>
        <Row className={classes.buttonsWrapper}>
          <Col className={classes.colCancel}>
            <UCButton
              onClick={() => setShowModal(false)}
              type="default"
              className={classes.button}
              text={<FormattedMessage id="guestHeader.modalUpload.cancel" />}
              disabled={isUploading}
            />
          </Col>
          <Col className={classes.colSubmit}>{submitButton}</Col>
        </Row>
      </div>
    );
  }

  const maxSize = 5242880;

  return (
    <Dropzone
      autoUpload={false}
      onChangeStatus={handleChangeStatus}
      getUploadParams={getUploadParams}
      LayoutComponent={customLayout}
      onSubmit={handleSubmit}
      maxFiles={1}
      accept=".csv"
      validate={(file) => {
        const regexp = /^[a-zA-Z0-9-_. ()]+$/;
        if (file.file.name.search(regexp) === -1) {
          return intl.formatMessage({ id: "dropzone.uploadGuest.invalidName" });
        }
      }}
      maxSizeBytes={maxSize}
      //minSizeBytes={minSize}
      classNames={{ dropzone: classes.dropzone }}
      PreviewComponent={props => <CustomPreview {...props} customValidationError={validationError} setIsUploading={setIsUploading}/>}
      InputComponent={props => <CustomInput {...props} />}
      SubmitButtonComponent={props => <CustomSubmitButton {...props} validationError={validationError} buttonClassName={classes.button} />}
    />
  )
}

export default GuestProfilesDropzone;